import React, { useState } from 'react';
import { TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface NumericFieldProps {
    label: string;
    value: number | undefined;
    decimalPlaces?: number; // Optional: specify the number of decimal places
    onChange: (value: number) => void;
}

const NumericField: React.FC<NumericFieldProps> = ({ label, value, decimalPlaces = 2, onChange }) => {
    const [isFocused, setIsFocused] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const { t } = useTranslation();

    // Format the number to a specific format (e.g., thousand separator, decimal places)
    const formatNumber = (num: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'decimal',
            maximumFractionDigits: decimalPlaces,
            minimumFractionDigits: decimalPlaces,
            useGrouping: true,
        }).format(num).replace(/,/g, ' ').replace('.', ',');
    };

    const handleDecimalKeyDown = (event: React.KeyboardEvent) => {
        const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
        const allowedKeysWithCTRL = ['c', 'x', 'v'];
        const isNumericOrComma = /^[0-9,.]$/;
        const key = event.key;

        if (!isNumericOrComma.test(key) && !allowedKeys.includes(key) && !(event.ctrlKey && allowedKeysWithCTRL.includes(key.toLowerCase()))) {
            event.stopPropagation();
            event.preventDefault();
        }

        if ((key === ',' || key === '.') && (inputValue.includes(',') || inputValue.includes('.'))) {
            event.stopPropagation();
            event.preventDefault();
        }
    };

    const handlePaste = (event: React.ClipboardEvent) => {
        const pasteData = event.clipboardData.getData('Text');
        if (!/^[0-9,.]*$/.test(pasteData)) {
            event.stopPropagation();
            event.preventDefault();
        }
    };

    const customRound = (num: number) => {
        const factor = Math.pow(10, decimalPlaces);
        return (num * factor % 1 >= 0.5 ? Math.ceil(num * factor) : Math.floor(num * factor)) / factor;
    };

    return (
        <TextField
            label={t(label)}
            variant="outlined"
            fullWidth
            size="small"
            type="text"
            value={isFocused ? inputValue : formatNumber(value ?? 0)}
            onFocus={() => {
                setInputValue((value ?? '0').toString().replace('.', ','));
                setIsFocused(true);
            }}
            onChange={(e) => {
                setInputValue(e.target.value);
            }}
            onKeyDown={handleDecimalKeyDown}
            onPaste={handlePaste}
            onBlur={(e) => {
                setIsFocused(false);
                const newValue = isNaN(parseFloat(e.target.value.replace(',', '.'))) ? 0 : customRound(parseFloat(e.target.value.replace(',', '.')));
                onChange(newValue);
            }}
        />
    );
};

export default NumericField;
